import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

interface Params {
  id: number;
}

type TData = void;
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// DELETE ums/v1/cms/master-product-attributes/values/:id
export function useDeleteAttributeValueApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id }: TVariables): Promise<TData> => {
    try {
      await http.delete(`${productAttributesEndpoint}/values/${id}`);
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/values/delete`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
